<template>
    <app-base-main parentTable>
      <template #filter-group>
        <app-filter-group
            @query="$gjNoticeQuery"
            @reset="$gjReset">
          <gj-warehouse-select
              v-model="filter.warehouseIds"
              :fba="false"
              multiple
          ></gj-warehouse-select>
          <gj-select
              v-model="filter.statusList"
              multiple
              collapse-tags
              :options="AssemblyOrderEnum.statusOption"
              placeholder="状态"
              width="140px"
          ></gj-select>
          <gj-search-input
              v-model="filter.code"
              width="150px"
              placeholder="装配单号"
          ></gj-search-input>
        </app-filter-group>
      </template>
      <template #operate-group>
        <gj-button
            v-if="$permission('assemble:add')"
            type="primary"
            @click="add"
        >添加装配单</gj-button>
        <gj-button
            v-if="$permission('assemble:abolish')"
            :disabled="noSelect || selectedSomeIsNot('status', AssemblyOrderEnum.status.DRAFT)"
            @click="cancel(selectedRows)"
        >作废</gj-button>
      </template>
      <template #tool-group>
        <gj-tool-group
            help
        ></gj-tool-group>
      </template>
      <template #table>
        <gj-table
            ref="gjTable"
            adaptParent
            :name="tableName"
            :loading="loading"
            :data="tableData"
            :columns="columns"
            selection
            :scroll-x="{gt: -1}"
            :scroll-y="{gt: -1}"
            :bak-selected-rows="bakSelectedRows"
            :bak-rows="selectedRows"
            @clearBakSelectedRows="clearBakRows"
            :selectable="(row) => row.$gjParentData  && !row.$gjExtendRow"
            :extend-row-config="{prop: 'extendRow'}"
            :structure-config="{type: 'ParentChild', children: 'items', parentSpan: [1, 4]}"
            sortable="custom"
            :pagination="pagination"
            :show-overflow="false"
            :default-sort="{prop: 'createTime', order: 'descending'}"
            @sort-change="$gjSortChange"
            @page-change="$gjNoticeQuery">
          <template v-slot:picture="{row}">
            <template v-if="row.$gjParentData">
              <div style="display: inline-block; width: 150px;">
                {{row.code | defaultShow}}
                <gj-copy-icon :content="row.code"></gj-copy-icon>
              </div>
              <div v-gutter="20" style="display: inline-block;">
                <span>仓库：{{row.warehouseName | defaultShow}}</span>
                <gj-tag
                    type="plain"
                    shape="rectangle"
                    :color="row.status | optionColor(AssemblyOrderEnum.statusOption)">
                  {{ row.status | optionText(AssemblyOrderEnum.statusOption) }}
                </gj-tag>
                <span><span class="parent-table-label">创建人：</span>{{row.createUserName | defaultShow}}</span>
                <span><span class="parent-table-label">创建时间：</span>{{row.createTime | defaultShow}}</span>
                <span><span class="parent-table-label">处理人：</span>{{row.assembleUserName | defaultShow}}</span>
                <span><span class="parent-table-label">完成时间：</span>{{row.completeTime | defaultShow}}</span>
                <div style="display: inline-block">
                  <gj-edit-input
                      v-if="$permission('assemble:add') && row.status === AssemblyOrderEnum.status.DRAFT"
                      :value="row.memo"
                      :item="row"
                      table-cell-edit
                      :on-confirm="updateMemo"
                      pop-title="备注">
                    <gj-text max-width="200px"><span class="parent-table-label">备注：</span>{{row.memo | defaultShow}}</gj-text>
                  </gj-edit-input>
                  <template v-else>
                    <span class="parent-table-label">备注：</span>{{row.memo | defaultShow}}
                  </template>
                </div>
              </div>
            </template>
            <template v-else>
              <gj-image :src="row.skuImageUrl"></gj-image>
            </template>
          </template>
          <template v-slot:sku="{row}">
            <div>{{row.productName | defaultShow}}</div>
            <div>{{row.sku | defaultShow}}</div>
          </template>
          <template v-slot:op="{ row }">
            <template v-if="row.$gjParentData">
              <gj-button-group
                  type="dropdown-op"
                  :config="opBtnConfig(row)"
              ></gj-button-group>
            </template>
          </template>
          <template v-slot:extend:row="{row}">
            <div class="default">
              <div v-if="!row.expand">
                <gj-link type="default" @click="openDetail(row,true)">
                  更多（共{{row.num}}条）
                  <i class="iconfont iconzhankai"></i>
                </gj-link>
              </div>
              <div v-else>
                <gj-link type="default" @click="openDetail(row, false)">
                  收起
                  <i class="iconfont iconshouqi"></i>
                </gj-link>
              </div>
            </div>
          </template>
        </gj-table>
      </template>
    </app-base-main>
</template>

<script>
  import {AssemblyOrderEnum} from "./enum";
  import {queryMixin, responseMixin, selectionMixin, valueCheckMixin, bakSelectRowsMixin} from "@/minxins";
  import {OpEnum} from "@/utils/options";
  import {
    cancelAssemblyOrder,
    confirmAssemblyOrder,
    getAssemblyOrderPage,
    updateAssemblyOrderMemo
  } from "@/api/warehouse";
  import AppOpAssemblyOrder from "./modal/op/op-assembly-order";

  const StatusEnum = AssemblyOrderEnum.status;

  export default {
    name: "app-assembly-order",
    mixins: [queryMixin, selectionMixin, responseMixin, valueCheckMixin, bakSelectRowsMixin],
    data() {
      return {
        tableName: 'assemblyOrder',
        tableData: [],
        columns: [
          {id: 'picture', header: '图片', field: 'slot:picture', width: '50px'},
          {id: 'sku', header: '产品名称/SKU', field: 'slot:sku', width: '300px'},
          {id: 'quantity', header: '装配数量', field: 'quantity', width: '80px', align: 'right'},
          {id: 'assembleRule', header: '组合规则', field: 'assembleRule', minWidth: '300px'},
          {id: 'op', header: '操作', field: 'slot:op', width: '120px'},
        ],
        opBtnConfig: (row) => [
          {
            label: '详情',
            click: () => this.view({...row, items: row.productsCopy}),
          },
          {
            label: '编辑',
            show: () => this.$permission('assemble:add')
              && row.status === StatusEnum.DRAFT,
            click: () => this.edit({...row, items: row.productsCopy}),
          },
          {
            label: '确认',
            show: () => this.$permission('assemble:add')
              && row.status === StatusEnum.DRAFT,
            click: () => this.confirm([{...row, items: row.productsCopy}]),
          },
          {
            label: '作废',
            show: () => this.$permission('assemble:abolish')
              && row.status === StatusEnum.DRAFT,
            click: () => this.cancel([{...row, items: row.productsCopy}]),
          },
          {
            label: '日志',
            click: () => this.viewLog({...row, items: row.productsCopy}),
          },
        ],
        filter: {
          warehouseIds: [],
          statusList: [],
          code: null
        },
        AssemblyOrderEnum: AssemblyOrderEnum,
        notStorage: true
      }
    },
    methods: {
      $gjCacheCallback(params) {
        return {
          warehouseIds: params.warehouseIds
        };
      },
      $gjQueryInitParams() {
        return {
          filter: {
            warehouseIds: [],
            statusList: [],
            code: null
          },
        };
      },
      $gjQuery() {
        // 构造查询参数
        const params = {
          model: {
            code: this.filter.code,
            statusList: this.filter.statusList,
            warehouseIds: this.filter.warehouseIds
          },
          ...this.queryPagination,
          ...this.querySort
        };
        this.globalParams = params;
        // 查询
        this.loading = true;
        getAssemblyOrderPage(params)
          .then((data) => {
            this.tableData = data.records.map((v, i) => {
              const items = this._.cloneDeep(v.items)
              return Object.assign(v, {
                productsCopy: items,
                items: items ? items.slice(0, 3) : [],
                expand: false,
                dataIndex: i
              })
            });
            this.pagination = this.getPagination(data);
          })
          .complete(() => {
            this.loading = false;
          });
      },
       add() {
        this.$gjCreate(AppOpAssemblyOrder, {
          props: {
            opType: OpEnum.type.ADD
          },
          on: {
            created: () => {
              this.$gjResetPage();
            }
          }
        });
      },
      view(row) {
        this.$gjCreate(AppOpAssemblyOrder, {
          props: {
            opType: OpEnum.type.VIEW,
            assemblyOrder: row
          }
        });
      },
      edit(row) {
        this.$gjCreate(AppOpAssemblyOrder, {
          props: {
            opType: OpEnum.type.UPDATE,
            assemblyOrder: row
          },
          on: {
            updated: () => {
              this.$gjResetPage();
            }
          }
        });
      },
      confirm(rows) {
        const modal = this.$gjCreate("gj-info-modal", {
          props: {
            content: rows.length === 1
              ? `确认[${rows[0].code}]吗？`
              : `已选中 ${rows.length} 个装配单，要批量确认吗？`,
            contentStyle: {'color': '#333', 'font-size': '12px'}
          },
          on: {
            cancel: () => modal.$gjDestroy(),
            confirm: () => {
              modal.confirmLoading = true;
              confirmAssemblyOrder(rows[0].id)
                .then(res => {
                  this.$gjResponseHandle(res, () => {
                    this.$gjMessage.success('操作成功');
                    this.$gjResetPage();
                    modal.$gjDestroy();
                  });
                })
                .complete(() => modal.confirmLoading = false);
            }
          },
        });
      },
      cancel(rows) {
        const modal = this.$gjCreate('gj-textarea-modal', {
          props: {
            header: rows.length > 1 ? '作废' : `作废<span class="modal-secondary-title">${rows[0].code}</span>`,
            label: '作废理由',
            labelWidth: '74px',
            contentStyle: {padding: '16px 20px 0 20px'},
            notMargin: true
          },
          on: {
            submit: (reason) => {
              modal.confirmLoading = true;
              cancelAssemblyOrder({
                ids: rows.map(row => row.id),
                abolishReason: reason
              })
                .then(res => {
                  this.$gjResponseHandle(res, () => {
                     this.$gjMessage.success('作废成功');
                     this.$gjResetPage();
                     modal.$gjDestroy();
                  });
                })
                .complete(() => modal.confirmLoading = false);
            }
          }
        })
      },
      updateMemo(memo, row, closeEditor, closeLoading) {
        updateAssemblyOrderMemo({
          id: row.id,
          memo
        })
          .then(() => {
            this.$gjMessage.success('操作成功');
            closeEditor();
            this.$gjResetPage();
          })
          .complete(() => closeLoading());
      },
      viewLog(row) {
        this.$gjCreate('gj-log-modal', {
          props: {
            url: `/supply/assemble/logPage`,
            params: {
              model: {
                assembleId: row.id
              }
            },
            columns: [
              {id: 'createDate', header: '操作时间', field: 'createTime', minWidth: '120px'},
              {id: 'opUser', header: '操作用户', field: 'createUserName', minWidth: '100px'},
              {id: 'op', header: '操作分类', field: 'option:op', options: [
                  {id: 'Add', text: '新增'},
                  {id: 'Update', text: '更新'},
                  {id: 'Abolish', text: '作废'},
                  {id: 'Complete', text: '完成'}
                ],
                minWidth: '120px'},
              {id: 'content', header: '操作内容', field: 'content', overflowLine: 2, minWidth: '300px'}
            ]
          }
        });
      },
      openDetail(row, flag){
        if (flag) {
          row.items = row.productsCopy;
          row.expand = flag;
        } else {
          row.items = row.items.slice(0,3);
          row.expand = flag;
        }
        this.tableData = this.tableData.map(v => {
          if (v.id === row.id) {
            v.expand = row.expand
            v.items = row.items
          }
          return v
        })
        this.bakSelectedRows = this._.cloneDeep(this.selectedRows);
      }
    }
  }
</script>

<style scoped>
.iconfont{
  font-size: 10px;
  color: #999;
}
.iconshouqi{
  margin-left: 5px;
}
</style>
